import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Alert, Skeleton } from '@mui/material';
import { useReducedMotion } from 'framer-motion';
import {
  Add as AddIcon,
  Calculate as MathIcon,
  Science as ScienceIcon,
  AccountBalance as HistoryIcon,
  Translate as LanguagesIcon,
  Edit as EnglishIcon,
  Palette as ArtsIcon,
  Computer as ComputingIcon,
  AutoAwesome as OtherIcon,
  Visibility as LiveIcon,
  VisibilityOff as DraftIcon,
  Explore as HintIcon,
  Timer as PacingIcon,
  School as ClassesIcon,
  ArrowForward as ArrowIcon,
  AutoStories as LibraryIcon,
} from '@mui/icons-material';
import { tutorTokens } from '../../../theme/tutorTheme';
import { listAllAgents, HINT_STYLES, PACING_OPTIONS, SUBJECTS } from '../../../utils/agentTutorStorage';
import AgentBuilderWizard from './AgentBuilderWizard';
import MathTutorConfig from './MathTutorConfig';

// Mirrors AgentBuilderWizard's SUBJECT_ICONS map (it isn't exported there) so
// subject identity reads the same in the library shelf as it does mid-build.
const SUBJECT_ICONS = {
  math: <MathIcon />,
  science: <ScienceIcon />,
  history: <HistoryIcon />,
  languages: <LanguagesIcon />,
  english: <EnglishIcon />,
  arts: <ArtsIcon />,
  computing: <ComputingIcon />,
  other: <OtherIcon />,
};

const SUBJECT_LABELS = SUBJECTS.reduce((acc, s) => ({ ...acc, [s.value]: s.label }), {});

// Card-scale shorthands of the full HINT_STYLES / PACING_OPTIONS copy, which
// is written for a settings screen ("Socratic — ask guiding questions"), not
// a scan-at-a-glance shelf tile.
const HINT_LABELS = { socratic: 'Socratic questions', nudge: 'Step-by-step nudges', worked_example: 'Worked examples' };
const PACING_LABELS = { mastery: 'Mastery-paced', schedule: 'Class-paced' };

// `practiceTests` is deliberately absent — see the matching comment in
// AgentBuilderWizard.js. It has no backend-mapped effect, so a legacy agent
// that still has it set shouldn't advertise it on its library card.
const ACTIVITY_LABELS = { flashcards: 'Flashcards', quizzes: 'Quizzes' };

const STATUS_META = {
  active: { icon: LiveIcon, label: 'Live', color: tutorTokens.correct, bg: tutorTokens.correctBg, border: tutorTokens.correctBorder },
  draft: { icon: DraftIcon, label: 'Draft', color: tutorTokens.partial, bg: tutorTokens.partialBg, border: tutorTokens.partialBorder },
};
// Anything unrecognised reads as a draft rather than crashing — an agent
// whose status we can't interpret is certainly not something to present as
// live to students.
const statusMeta = (status) => STATUS_META[status] || STATUS_META.draft;

const focusRingSx = {
  '&:focus-visible': { outline: `2px solid ${tutorTokens.accent}`, outlineOffset: 2 },
};

// One teaching instrument on the shelf: who it's for, how it teaches, and
// what it's actually doing right now — not just its name in a box.
const AgentCard = ({ agent, onOpen }) => {
  const status = statusMeta(agent.status);
  const StatusIcon = status.icon;
  const isLive = agent.status === 'active';
  const classCount = (agent.assignedGroupIds || []).length;
  const subjectIcon = SUBJECT_ICONS[agent.subject] || SUBJECT_ICONS.other;
  const subjectLabel = SUBJECT_LABELS[agent.subject] || 'General';
  const hintLabel = HINT_LABELS[agent.hintStyle] || HINT_STYLES.find((h) => h.value === agent.hintStyle)?.label;
  const pacingLabel = PACING_LABELS[agent.pacing] || PACING_OPTIONS.find((p) => p.value === agent.pacing)?.label;
  const extraActivities = Object.keys(ACTIVITY_LABELS).filter((key) => !!agent.activities?.[key]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Configure ${agent.name || 'Untitled agent'} — ${status.label}, assigned to ${classCount} class${classCount === 1 ? '' : 'es'}`}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 2.5,
        borderRadius: `${tutorTokens.radiusLg}px`,
        backgroundColor: tutorTokens.surfaceRaised,
        border: `1px solid ${isLive ? tutorTokens.borderStrong : tutorTokens.border}`,
        boxShadow: isLive ? tutorTokens.shadowCard : 'none',
        cursor: 'pointer',
        transition: `border-color ${tutorTokens.durFast}s ease, box-shadow ${tutorTokens.durFast}s ease, transform ${tutorTokens.durFast}s ease`,
        '&:hover': { borderColor: tutorTokens.accent, boxShadow: tutorTokens.shadowHover, transform: 'translateY(-2px)' },
        '&:active': { transform: 'translateY(-1px) scale(0.99)' },
        // The lift is spatial movement; the border/shadow state change alone
        // still reads as "hovered" without it. Keep the tiny press-scale —
        // it's confirmation, not motion.
        '@media (prefers-reduced-motion: reduce)': {
          '&:hover': { transform: 'none' },
          '&:active': { transform: 'scale(0.99)' },
        },
        ...focusRingSx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: `${tutorTokens.radiusMd}px`,
              backgroundColor: tutorTokens.accentLight,
              color: tutorTokens.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {subjectIcon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontFamily: tutorTokens.fontUI, ...tutorTokens.type.subtitle, color: tutorTokens.ink }}
            >
              {agent.name || 'Untitled agent'}
            </Typography>
            <Typography sx={{ ...tutorTokens.type.label, color: tutorTokens.inkSoft }}>
              {subjectLabel} · {(agent.grades || []).join(', ') || 'K–12'}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.35,
            borderRadius: 999,
            flexShrink: 0,
            backgroundColor: status.bg,
            border: `1px solid ${status.border}`,
            color: status.color,
            ...tutorTokens.type.label,
            fontWeight: 700,
          }}
        >
          <StatusIcon sx={{ fontSize: 14 }} aria-hidden="true" />
          {status.label}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
        {hintLabel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...tutorTokens.type.label, fontWeight: 500, color: tutorTokens.inkSoft }}>
            <HintIcon sx={{ fontSize: 15, color: tutorTokens.inkFaint }} aria-hidden="true" />
            {hintLabel}
          </Box>
        )}
        {pacingLabel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...tutorTokens.type.label, fontWeight: 500, color: tutorTokens.inkSoft }}>
            <PacingIcon sx={{ fontSize: 15, color: tutorTokens.inkFaint }} aria-hidden="true" />
            {pacingLabel}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...tutorTokens.type.label, fontWeight: 500, color: tutorTokens.inkSoft }}>
          <ClassesIcon sx={{ fontSize: 15, color: tutorTokens.inkFaint }} aria-hidden="true" />
          {classCount} class{classCount === 1 ? '' : 'es'}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1, mt: 'auto', borderTop: `1px solid ${tutorTokens.divider}`, flexWrap: 'wrap' }}>
        <Typography sx={{ ...tutorTokens.type.label, fontWeight: 500, color: tutorTokens.inkFaint }}>
          {extraActivities.length
            ? extraActivities.map((k) => ACTIVITY_LABELS[k]).join(' · ')
            : 'Step-by-step only'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ ...tutorTokens.type.label, fontWeight: 700, color: tutorTokens.accent, display: 'flex', alignItems: 'center', gap: 0.25 }}>
          Configure <ArrowIcon sx={{ fontSize: 14 }} />
        </Typography>
      </Box>
    </Box>
  );
};

const EXPLAINER_POINTS = [
  { icon: HintIcon, title: 'Hints your way', body: 'Socratic questions, small nudges, or worked examples — you pick.' },
  { icon: PacingIcon, title: 'Paced your way', body: 'Mastery-based, or matched to your weekly class plan.' },
  { icon: ClassesIcon, title: 'Live for one class in one click', body: 'Publish it, assign it, and it starts helping students that day.' },
];

// The one create action on the page — used to be duplicated (a header
// button *and* a dashed "Build Another Agent" tile competing with the
// agent list for the same grid row), and only rendered at all when the
// library was empty, so a teacher with agents never saw the "how it
// works"/"no tech skills needed" explanation at all. Now it's the
// standing top block on every visit: agents listed below it, not beside
// it — which also means a shelf of one or two agents has a real reason to
// take up the top of the page instead of a grid that ends early and
// leaves the rest blank.
// Explicit two-column structure — icon column, then a single content
// column that owns everything else (heading, body, the three value
// props, the button). The previous version put the icon and the
// heading/body in one flex row (indenting them past the icon) while the
// props row and the button sat as separate top-level children starting
// back at the card's own padding edge — four different left edges in one
// card, none of them related to each other. Everything in the content
// column below shares exactly one edge now, because it's the same flex
// item, not four siblings guessing at the same offset independently.
const CreateAgentPanel = ({ hasAgents, onCreate }) => (
  <Box
    sx={{
      borderRadius: `${tutorTokens.radiusXl}px`,
      backgroundColor: tutorTokens.surfaceRaised,
      border: `1px solid ${tutorTokens.border}`,
      boxShadow: tutorTokens.shadowCard,
      p: { xs: 3, sm: 5 },
      mb: tutorTokens.layout.sectionGap,
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          width: 64,
          height: 64,
          flexShrink: 0,
          borderRadius: `${tutorTokens.radiusLg}px`,
          backgroundColor: tutorTokens.accentLight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LibraryIcon sx={{ color: tutorTokens.accent, fontSize: 30 }} aria-hidden="true" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontFamily: tutorTokens.fontUI, ...tutorTokens.type.title, color: tutorTokens.ink, mb: 0.75 }}>
          An agent is a tutor you design, not one you pick from a menu
        </Typography>
        {/* Folds in the dashed tile's old supporting line ("No tech
            skills needed…") rather than dropping it — this is the only
            place on the page that says building an agent doesn't require
            technical skill, and that reassurance still earns its place
            even once a teacher already has agents. `58ch`, not the old
            560px fixed value — that was under half this card's own width
            regardless of how wide the card itself got, which is exactly
            why it broke into three ragged lines with the whole right
            side of the card empty. `ch` still caps this at a genuinely
            readable measure (mid-60s to low-70s real characters per
            line, the same calibration used for MathTutorConfig's
            misconception rows), it's just sized to actually use a
            meaningful share of this card instead of a fraction of it. */}
        <Typography sx={{ ...tutorTokens.type.bodyLg, color: tutorTokens.inkSoft, maxWidth: '58ch' }}>
          No tech skills needed — answer a few questions about how you teach: how you hint, how strict your
          feedback is, how fast a class should move — and it works problems with your students exactly that way.
        </Typography>

        {/* Equal grid columns spanning the content column's real width,
            not three flex items fixed at 200px (and a `maxWidth: 230`
            on the row on top of that) bunched at its left edge. At 200px,
            "Live for one class in one click" wrapped its own title to two
            lines and body to four; "Hints your way" ended on an orphaned
            "— you pick." Letting each prop actually claim a third of the
            available width fixes both. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: { xs: 2.5, sm: 4 }, mt: 3.5, mb: 3.5 }}>
          {EXPLAINER_POINTS.map(({ icon: Icon, title, body }) => (
            <Box key={title} sx={{ display: 'flex', gap: 1.25, minWidth: 0 }}>
              <Icon sx={{ color: tutorTokens.accent, fontSize: 20, mt: 0.25, flexShrink: 0 }} aria-hidden="true" />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...tutorTokens.type.body, fontWeight: 700, color: tutorTokens.ink }}>{title}</Typography>
                <Typography sx={{ ...tutorTokens.type.body, color: tutorTokens.inkSoft }}>{body}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreate}
          sx={{
            backgroundColor: tutorTokens.accent,
            fontWeight: 700,
            px: 3,
            py: 1.1,
            minHeight: 44,
            boxShadow: tutorTokens.shadowCard,
            transition: `background-color ${tutorTokens.durFast}s ease, box-shadow ${tutorTokens.durFast}s ease, transform ${tutorTokens.durFast}s ease`,
            '&:hover': { backgroundColor: tutorTokens.accentDark, boxShadow: tutorTokens.shadowHover },
            '&:active': { transform: 'scale(0.97)' },
            ...focusRingSx,
          }}
        >
          {hasAgents ? 'Build Your Own Agent' : 'Build Your First Agent'}
        </Button>
      </Box>
    </Box>
  </Box>
);

const CardSkeleton = ({ reduceMotion }) => (
  <Box
    sx={{
      p: 2.5,
      borderRadius: `${tutorTokens.radiusLg}px`,
      border: `1px solid ${tutorTokens.border}`,
      backgroundColor: tutorTokens.surfaceRaised,
    }}
  >
    <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
      <Skeleton
        variant="rounded"
        width={40}
        height={40}
        animation={reduceMotion ? false : 'pulse'}
        sx={{ borderRadius: `${tutorTokens.radiusMd}px`, bgcolor: tutorTokens.border }}
      />
      <Box sx={{ flex: 1 }}>
        <Skeleton animation={reduceMotion ? false : 'pulse'} width="70%" height={22} sx={{ bgcolor: tutorTokens.border }} />
        <Skeleton animation={reduceMotion ? false : 'pulse'} width="45%" height={16} sx={{ bgcolor: tutorTokens.border }} />
      </Box>
    </Box>
    <Skeleton animation={reduceMotion ? false : 'pulse'} height={16} width="80%" sx={{ mb: 0.75, bgcolor: tutorTokens.border }} />
    <Skeleton animation={reduceMotion ? false : 'pulse'} height={16} width="55%" sx={{ bgcolor: tutorTokens.border }} />
  </Box>
);

// Teacher-facing Agent Tutor tab.
// Scope note: the mockup shows a full library of five pre-built agents plus
// a Teacher Marketplace. Per the SOW (Exhibit A — Out of Scope), only the
// Math Tutor showcase agent and the Build Your Own Agent flow are in scope
// for this build, so the library only ever shows those two tiles.
const AgentTutorView = ({ currentUser, groups = [] }) => {
  const navigate = useNavigate();
  const reduceMotion = !!useReducedMotion();
  const [view, setView] = useState('library'); // library | builder | config
  const [agents, setAgents] = useState([]);
  // Which agent the builder/config view is editing. null in the builder means
  // "create a new one" — the server assigns the id on save.
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setAgents(await listAllAgents());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const backToLibrary = async () => {
    await refresh();
    setView('library');
  };

  // Live agents read first — they're the ones actually in front of students
  // right now, so they earn the top of the shelf over an unfinished draft.
  const sortedAgents = useMemo(
    () => [...agents].sort((a, b) => (a.status === 'active' ? 0 : 1) - (b.status === 'active' ? 0 : 1)),
    [agents]
  );

  // The single pre-built K-12 Math showcase agent, if this deployment has
  // one (see app/services/showcase_agent.py). `undefined` while `agents` is
  // still loading, `null` once loaded if none came back — the wizard treats
  // "loading" and "genuinely absent" the same (template not offered yet),
  // so we never flash "unavailable" while the initial fetch is in flight.
  // `error` (surfaced separately as `showcaseFetchFailed`) is what lets the
  // wizard tell "no template exists" apart from "couldn't check" — both
  // collapse to `showcaseAgent: null` here, but they're different messages
  // for a teacher.
  const showcaseAgent = useMemo(() => {
    if (loading) return undefined;
    return agents.find((a) => a.isShowcase) || null;
  }, [agents, loading]);

  if (view === 'builder') {
    return (
      <AgentBuilderWizard
        currentUser={currentUser}
        groups={groups}
        existingAgent={selectedAgent || undefined}
        showcaseAgent={showcaseAgent}
        showcaseFetchFailed={!!error}
        onCancel={() => setView('library')}
        onSaveDraft={backToLibrary}
        onPublished={backToLibrary}
      />
    );
  }

  if (view === 'config' && selectedAgent) {
    return (
      <MathTutorConfig
        agent={selectedAgent}
        groups={groups}
        currentUser={currentUser}
        onBack={backToLibrary}
        // MathTutorConfig may have just saved `local` (dirty edits saved
        // before navigating away, or the untouched agent when not dirty) —
        // that object, not the possibly-stale `selectedAgent` this view
        // mounted config with, is what the builder must open on. Without
        // this, a save-then-edit-in-builder round trip silently reverted:
        // the save landed on the server, but the wizard reopened on the
        // pre-edit object and PUT it straight back over the edit.
        onOpenBuilder={(updatedAgent) => {
          if (updatedAgent) setSelectedAgent(updatedAgent);
          setView('builder');
        }}
        // Opens the real tutoring session for this agent, which is what a
        // teacher actually wants to see. Previously this reopened the builder
        // wizard at step 1 — the same thing onOpenBuilder does — so the button
        // never took anyone to a test drive at all.
        onTestDrive={(agentId) => navigate(`/tutor/${agentId}`)}
      />
    );
  }

  const startNewAgent = () => {
    setSelectedAgent(null);
    setView('builder');
  };

  return (
    // No `minHeight: '100vh'` — this view only ever mounts inside
    // AdminDashboardV2's `component="main"`, which already reserves the
    // full viewport height and the same background color; a second
    // `100vh` here just made a short library (a couple of agents) sit
    // inside a page taller than the viewport for no reason.
    <Box sx={{ maxWidth: tutorTokens.layout.maxWidth, mx: 'auto', p: tutorTokens.layout.gutter, backgroundColor: tutorTokens.surface }}>
      <Box sx={{ mb: 3.5 }}>
        {/* "Your Agents", not "Agent Tutor" — the admin shell's own top
            bar already reads "Agent Tutor" (from the sidebar nav item),
            directly above this. Repeating the exact same two words as
            the in-page heading answered "where am I" twice and "what's
            here" not at all; naming what this specific screen shows
            (the shelf of agents themselves) does both. */}
        <Typography variant="h4" sx={{ fontWeight: 600, color: tutorTokens.ink }}>
          Your Agents
        </Typography>
        <Typography sx={{ ...tutorTokens.type.bodyLg, color: tutorTokens.inkSoft, mt: 0.25 }}>
          AI teaching agents your teachers design, customize, and share
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2.5, borderRadius: `${tutorTokens.radiusSm}px` }}>
          {error}
        </Alert>
      )}

      {/* The create action lives here now, once, as its own block above
          the list — it used to be duplicated (a header button *and* a
          dashed "Build Another Agent" tile competing with the agent list
          for the same grid row). Rendered on every visit, not just when
          the library is empty, so agents are always "the panel, then the
          list" rather than the list occasionally growing a create tile of
          its own. */}
      <CreateAgentPanel hasAgents={agents.length > 0} onCreate={startNewAgent} />

      {/* `alignItems: 'start'`, not the grid default `stretch` — stretch
          forced every card in a row to match the row's tallest item (the
          create tile before its own minHeight came off, or just a card
          with more meta chips wrapping to a second line), which is what
          left real agent cards with an empty band between their meta row
          and their footer. Each card now sizes to its own content; a shelf
          of two or three agents no longer manufactures voids to fill a
          taller neighbor's height. */}
      {loading && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2.5, alignItems: 'start' }}>
          {[0, 1, 2].map((i) => (
            <CardSkeleton key={i} reduceMotion={reduceMotion} />
          ))}
        </Box>
      )}

      {!loading && agents.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2.5, alignItems: 'start' }}>
          {sortedAgents.map((agent) => (
            <AgentCard key={agent.id} agent={agent} onOpen={() => { setSelectedAgent(agent); setView('config'); }} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AgentTutorView;
